"use client";

import { useState, useContext, useEffect } from "react";
import FormLayout from "../../layouts/FormLayout";
import TextInput from "../input/TextInput";
import TextArea from "../input/TextArea";
import SelectInput from "../input/SelectInput";
import DefaultButton from "../buttons/DefaultButton";
import { ApiContext } from "../contexts/ApiContext";
import { ValidationContext } from "../contexts/ValidationContext";

function ContactPage() {
    const { Url } = useContext(ApiContext);
    const { nameRegex, nameErrorMessage } = useContext(ValidationContext);

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [date, setDate] = useState('');
    const [startTime, setStartTime] = useState('');
    const [address, setAddress] = useState('');
    const [city, setCity] = useState('');
    const [zipCode, setZipCode] = useState('');
    const [homeVisitCategoryID, setHomeVisitCategoryID] = useState('');
    const [comments, setComments] = useState('');
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState(''); // 'success' or 'error'
    const [categories, setCategories] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        // Fetch public home visit categories
        fetch(Url + 'contact/home-visit-categories', {
            method: 'GET',
            headers: {
                'content-type': 'application/json',
            },
        })
            .then((res) => res.json())
            .then((data) => {
                if (data) {
                    setCategories(data);
                }
            })
            .catch((error) => {
                console.error('Error fetching categories:', error);
            });
    }, [Url]);

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        setIsSubmitting(true);
        setMessage('');

        // Convert date and time strings to ISO format for the API
        const dateTimeStart = new Date(`${date}T${startTime}Z`);
        const dateOnly = new Date(date);

        if (!firstName || !lastName || !email || !phone || !date || !startTime || !address || !city || !zipCode || !homeVisitCategoryID) {
            setMessageType('error');
            setMessage('Vul alle verplichte velden in.');
            setIsSubmitting(false);
            return;
        }

        fetch(Url + 'contact/home-visit-request', {
            method: 'POST',
            headers: {
                'content-type': 'application/json',
            },
            body: JSON.stringify({
                FirstName: firstName,
                LastName: lastName,
                Email: email,
                Phone: phone,
                Date: dateOnly,
                StartTime: dateTimeStart,
                Address: address,
                City: city,
                ZipCode: zipCode,
                HomeVisitCategoryID: parseInt(homeVisitCategoryID),
                Comments: comments,
            }),
        })
            .then((res) => {
                if (res.status === 200 || res.status === 201) {
                    setMessageType('success');
                    // Clear form
                    setFirstName('');
                    setLastName('');
                    setEmail('');
                    setPhone('');
                    setDate('');
                    setStartTime('');
                    setAddress('');
                    setCity('');
                    setZipCode('');
                    setHomeVisitCategoryID('');
                    setComments('');
                } else {
                    setMessageType('error');
                }
                return res.json();
            })
            .then((data) => {
                setMessage(data.message || 'Er is iets misgegaan. Probeer het opnieuw.');
            })
            .catch((error) => {
                setMessageType('error');
                setMessage('Er is een fout opgetreden. Probeer het later opnieuw.');
                console.error('Error:', error);
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    return (
        <FormLayout>
            <div className="space-y-6">
                <h1 className="text-2xl font-bold text-gray-900 mb-4">
                    Huisbezoek Aanvragen
                </h1>
                <p className="text-base leading-relaxed text-gray-500">
                    Vul onderstaand formulier in om een huisbezoek aan te vragen.
                    Wij nemen zo spoedig mogelijk contact met je op.
                </p>

                {message && (
                    <p className={`text-base leading-relaxed ${messageType === 'success' ? 'text-green-500' : 'text-red-500'}`}>
                        {message}
                    </p>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <TextInput
                            labelName="Voornaam *"
                            inputName="firstName"
                            onChange={(e) => setFirstName(e.target.value)}
                            value={firstName}
                            placeholder="Voornaam"
                            errorMessage={nameErrorMessage}
                            pattern={nameRegex}
                            type="text"
                        />
                        <TextInput
                            labelName="Achternaam *"
                            inputName="lastName"
                            onChange={(e) => setLastName(e.target.value)}
                            value={lastName}
                            placeholder="Achternaam"
                            errorMessage={nameErrorMessage}
                            pattern={nameRegex}
                            type="text"
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <TextInput
                            labelName="E-mailadres *"
                            inputName="email"
                            onChange={(e) => setEmail(e.target.value.toLowerCase())}
                            value={email}
                            placeholder="voorbeeld@email.nl"
                            errorMessage="Vul een geldig e-mailadres in."
                            type="email"
                        />
                        <TextInput
                            labelName="Telefoonnummer *"
                            inputName="phone"
                            onChange={(e) => setPhone(e.target.value)}
                            value={phone}
                            placeholder="06-12345678"
                            errorMessage="Vul een geldig telefoonnummer in (minimaal 10 tekens)."
                            pattern=".{10,50}"
                            type="tel"
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <TextInput
                            labelName="Datum van bezoek *"
                            inputName="date"
                            onChange={(e) => setDate(e.target.value)}
                            value={date}
                            placeholder=""
                            type="date"
                        />
                        <TextInput
                            labelName="Gewenste starttijd *"
                            inputName="startTime"
                            onChange={(e) => setStartTime(e.target.value)}
                            value={startTime}
                            placeholder=""
                            type="time"
                        />
                    </div>

                    <TextInput
                        labelName="Adres *"
                        inputName="address"
                        onChange={(e) => setAddress(e.target.value)}
                        value={address}
                        placeholder="Straatnaam 123"
                        errorMessage="Vul een geldig adres in (minimaal 5 tekens)."
                        pattern=".{5,255}"
                        type="text"
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <TextInput
                            labelName="Postcode *"
                            inputName="zipCode"
                            onChange={(e) => setZipCode(e.target.value)}
                            value={zipCode}
                            placeholder="1234ab"
                            errorMessage="Vul een geldige postcode in (4-10 tekens)."
                            pattern=".{4,10}"
                            type="text"
                        />
                        <TextInput
                            labelName="Plaats *"
                            inputName="city"
                            onChange={(e) => setCity(e.target.value)}
                            value={city}
                            placeholder="Plaatsnaam"
                            errorMessage={nameErrorMessage}
                            pattern={nameRegex}
                            type="text"
                        />
                    </div>

                    <SelectInput
                        labelName="Type huisbezoek *"
                        inputName="homeVisitCategoryID"
                        onChange={(e) => setHomeVisitCategoryID(e.target.value)}
                        value={homeVisitCategoryID}
                        options={categories.map((cat: any) => ({
                            ID: cat.ID,
                            Name: `${cat.Name} - €${cat.SalesPrice}`
                        }))}
                        errorMessage="Selecteer een type huisbezoek."
                    />

                    <TextArea
                        labelName="Opmerkingen"
                        inputName="comments"
                        onChange={(e) => setComments(e.target.value)}
                        value={comments}
                        placeholder="Eventuele opmerkingen of vragen..."
                        rows="4"
                    />
                    {message && (
                        <p className={`text-base leading-relaxed ${messageType === 'success' ? 'text-green-500' : 'text-red-500'}`}>
                            {message}
                        </p>
                    )}
                    <div className="mt-6 flex justify-center">
                        <DefaultButton
                            onClick={handleSubmit}
                            content={isSubmitting ? "Verzenden..." : "Verstuur Aanvraag"}
                            disabled={isSubmitting}
                        />
                    </div>

                </form>
            </div>
        </FormLayout>
    );
}

export default ContactPage;